/**
 * <nocturne-moon-view> — the round eyepiece view on the home page opening (sections/nocturne-opening.liquid):
 * the Moon at ×100 along its shadow line, drifting slowly across the field as it does in a telescope without
 * a motor, with the faint shimmer of the air. Uses the shared texture from nocturne-moon.js and only
 * animates while on screen.
 */
(() => {
  if (customElements.get('nocturne-moon-view')) return;

  class NocturneMoonView extends HTMLElement {
    connectedCallback() {
      this.canvas = this.querySelector('canvas');
      this.ctx = this.canvas.getContext('2d');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onResize = () => {
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        this.canvas.width = this.canvas.clientWidth * dpr;
        this.canvas.height = this.canvas.clientHeight * dpr;
        this.draw(performance.now());
      };
      window.addEventListener('resize', this.onResize);

      // The opening is on screen at load: build the texture once the page has settled.
      const build = () => {
        this.moon = window.NocturneMoon?.texture(1024);
        if (!this.moon) return;
        this.classList.add('is-ready');
        this.draw(performance.now());
      };
      if ('requestIdleCallback' in window) requestIdleCallback(build, { timeout: 1200 });
      else setTimeout(build, 300);

      this.io = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible) this.loop();
      });
      this.io.observe(this);
      this.onResize();
    }

    disconnectedCallback() {
      this.visible = false;
      this.io?.disconnect();
      window.removeEventListener('resize', this.onResize);
    }

    loop() {
      if (this.running || this.reduced) return;
      this.running = true;
      const tick = (now) => {
        if (!this.visible) { this.running = false; return; }
        this.draw(now);
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }

    draw(now) {
      const { ctx, canvas } = this;
      const S = canvas.width;
      if (!S) return;
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, S, S);

      if (this.moon) {
        // The view sits on the shadow line (the texture's terminator is left of centre), where craters stand out.
        const t = this.reduced ? 0 : now / 1000;
        const D = S * 2.8;
        const drift = Math.sin(t * 0.12) * S * 0.05;
        const shimmer = (Math.sin(t * 9) + Math.sin(t * 13.3)) * S * 0.0012;
        const cx = S / 2 + D * 0.21 + drift + shimmer;
        const cy = S / 2 + D * 0.04 + drift * 0.4;
        ctx.drawImage(this.moon, cx - D / 2, cy - D / 2, D, D);
      }

      // Field stop: the dark rim of the eyepiece.
      const rim = ctx.createRadialGradient(S / 2, S / 2, S * 0.32, S / 2, S / 2, S * 0.5);
      rim.addColorStop(0, 'rgba(0,0,0,0)');
      rim.addColorStop(1, 'rgba(0,0,0,.92)');
      ctx.fillStyle = rim;
      ctx.fillRect(0, 0, S, S);
    }
  }

  customElements.define('nocturne-moon-view', NocturneMoonView);
})();
